import * as React from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import styled, { css } from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { Icon } from '@progress/kendo-react-common';
import { generateValidations, MaterialUiFieldProps } from '../../core/form';
import { hasValue } from '../../lib/lib';
import { borderGrey, brandingColourHeavy, errorRed, labelGrey, popupZIndex } from '../../appComponents/cssConstants';
import PrintOnlyField from '../PrintOnlyField';
import { DisplayNoneOnPrint } from '../PrintStyles';

const DropDownListField = (props: Props) => {
	const { label, name, data, displayInLineLabel = true, className = '', disabled } = props;
	const {
		formState: { errors }
	} = useFormContext();
	const { field } = useController({ name, rules: generateValidations(props) });
	const labelId = `${name}Label`;
	const hasError = hasValue(errors[name]);

	return (
		<StyledFormControl
			variant="filled"
			fullWidth
			size="small"
			className={className}
			$displayInLineLabel={displayInLineLabel}
		>
			{label && (
				<StyledLabel id={labelId} error={hasError} $displayInLineLabel={displayInLineLabel}>
					{label}
				</StyledLabel>
			)}
			<StyledSelect
				name={name}
				id={name}
				labelId={labelId}
				value={field.value ?? ''}
				onChange={(e: any) => field.onChange(e.target.value)}
				className={hasError && 'error'}
				MenuProps={{ sx: { zIndex: popupZIndex } }}
				$displayInLineLabel={displayInLineLabel}
				disabled={disabled}
			>
				{data?.map((x) => (
					<MenuItem
						key={x.value}
						value={x.value}
						sx={{ color: 'black', fontSize: 16, lineHeight: '18px', minHeight: '40px' }}
					>
						{x.text}
					</MenuItem>
				))}
			</StyledSelect>
			<FormHelperText error={hasError}>{errors[name]?.message as any}</FormHelperText>
			<PrintOnlyField label={label} value={data?.find((x) => x.value === field.value)?.text} />
		</StyledFormControl>
	);
};

export default DropDownListField;

export const DropDownList = ({
	label,
	name,
	data,
	value,
	onSelectChange,
	displayInLineLabel = true,
	className = ''
}: DropDownListProps) => {
	const labelId = `${name}Label`;

	return (
		<StyledFormControl variant="filled" fullWidth size="small" className={className}>
			<StyledLabel id={labelId} $displayInLineLabel={displayInLineLabel}>
				{label}
			</StyledLabel>
			<StyledSelect
				name={name}
				id={name}
				labelId={labelId}
				value={value ?? ''}
				onChange={(e: any) => onSelectChange(e.target.value)}
				MenuProps={{ sx: { zIndex: popupZIndex } }}
				$displayInLineLabel={displayInLineLabel}
			>
				{data?.map((x) => (
					<MenuItem
						key={x.value}
						value={x.value}
						sx={{ color: 'black', fontSize: 16, lineHeight: '18px', minHeight: '40px' }}
					>
						{x.text} {x.isCompleted ? <StyledIcon name="check" /> : null}
					</MenuItem>
				))}
			</StyledSelect>
		</StyledFormControl>
	);
};

interface Props extends MaterialUiFieldProps {
	data: DropdownlistStatus<string | number>[];
	displayInLineLabel?: boolean;
}

interface DropDownListProps extends Props {
	value: string;
	onSelectChange: (value: unknown) => void;
	className?: string;
}

export interface DropdownlistStatus<T> {
	text: string;
	value: T;
	isCompleted?: boolean;
}

const StyledFormControl = styled(FormControl)<{ $displayInLineLabel?: boolean }>`
	margin-bottom: 10px;
	vertical-align: ${(props) => (props.$displayInLineLabel ? 'top' : 'bottom')};

	.MuiFormHelperText-root {
		&.Mui-error {
			font-size: 10px;
			font-weight: 500;
			margin: 5px 0 0 0;
			border-color: ${errorRed};
			color: ${errorRed};
		}
	}
`;

const StyledLabel = styled(InputLabel)<{ $displayInLineLabel: boolean }>`
	color: ${labelGrey};

	&.Mui-focused {
		color: ${brandingColourHeavy};
	}

	&.Mui-error {
		color: ${errorRed};
	}

	${(props) =>
		!props.$displayInLineLabel &&
		css`
			position: relative;
			max-width: 100%;
			transform: none;
			white-space: initial;
			font-size: 18px;
			line-height: 21px;
			margin: 6px 0;
		`}

	${DisplayNoneOnPrint}
`;

const StyledSelect = styled(Select)<{ $displayInLineLabel: boolean }>`
	border: 1px solid ${borderGrey};
	border-radius: 12px;
	background-color: white;
	font-size: 18px;
	color: black;

	&:before,
	&:after {
		display: none;
	}

	&:hover {
		border-color: ${labelGrey};
		background-color: white;
	}

	&.Mui-focused {
		border-color: ${brandingColourHeavy};
		background-color: white;
		border-width: 1px;
	}

	&.error {
		border-color: ${errorRed};
	}

	.MuiInputBase-input {
		text-align: left;

		${(props) =>
			!props.$displayInLineLabel &&
			css`
				padding-top: 12px;
				padding-bottom: 13px;
			`}
	}

	${DisplayNoneOnPrint}
`;

const StyledIcon = styled(Icon)`
	margin-left: 5px;
	color: #128504;
`;
